import React from 'react';
import { Alert, Box, FormControlLabel, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { JsonInput } from '@shared/components/JsonInput';
import CustomSwitch from '@shared/components/forms/theme-elements/CustomSwitch';
import CustomTextField from '@shared/components/forms/theme-elements/CustomTextField';

import {
  NoiseCancellationDraft,
  NoiseCancellationValidation,
} from '../utils/noiseProcessing.utils';

type NoiseCancellationEditorProps = {
  value: NoiseCancellationDraft;
  onChange: (value: NoiseCancellationDraft) => void;
  errors?: NoiseCancellationValidation;
  disabled?: boolean;
  warning?: string | null;
  showConfiguredToggle?: boolean;
};

export const NoiseCancellationEditor: React.FC<NoiseCancellationEditorProps> = ({
  value,
  onChange,
  errors = {},
  disabled = false,
  warning,
  showConfiguredToggle = true,
}) => {
  const { t } = useTranslation();
  const update = (patch: Partial<NoiseCancellationDraft>) => onChange({ ...value, ...patch });
  const fieldsDisabled = disabled || !value.configured;
  const message = (key?: string) => (key ? t(key) : undefined);

  return (
    <Stack spacing={2}>
      {warning ? <Alert severity="info">{warning}</Alert> : null}
      {showConfiguredToggle ? (
        <Box>
          <FormControlLabel
            control={
              <CustomSwitch
                checked={value.configured}
                disabled={disabled}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  update({ configured: event.target.checked })
                }
              />
            }
            label={t('noiseProcessing.cancellation.configured')}
          />
          {!value.configured ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('noiseProcessing.cancellation.configuredHelper')}
            </Typography>
          ) : null}
        </Box>
      ) : null}
      <CustomTextField
        label={t('noiseProcessing.cancellation.provider')}
        value={value.provider}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          update({ provider: event.target.value })
        }
        disabled={fieldsDisabled}
        error={Boolean(errors.provider)}
        helperText={message(errors.provider)}
        fullWidth
      />
      <CustomTextField
        label={t('noiseProcessing.cancellation.strength')}
        type="number"
        value={value.strength}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          update({ strength: event.target.value })
        }
        inputProps={{ min: 0, max: 1, step: 0.01 }}
        disabled={fieldsDisabled}
        error={Boolean(errors.strength)}
        helperText={message(errors.strength) ?? t('noiseProcessing.cancellation.strengthHelper')}
      />
      <FormControlLabel
        control={
          <CustomSwitch
            checked={value.recordAudio}
            disabled={fieldsDisabled}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              update({ recordAudio: event.target.checked })
            }
          />
        }
        label={t('noiseProcessing.cancellation.recordAudio')}
        sx={{ gap: 1, mt: 1.5 }}
      />
      <Box>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
          {t('noiseProcessing.cancellation.noiseDetection')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('noiseProcessing.cancellation.noiseDetectionDescription')}
        </Typography>
        <Box aria-live="polite" aria-atomic="true">
          <JsonInput
            value={value.noiseDetection}
            onChange={(noiseDetection) => update({ noiseDetection })}
            error={message(errors.noiseDetection)}
            disabled={fieldsDisabled}
            rows={5}
            placeholder={t('noiseProcessing.jsonPlaceholder')}
          />
        </Box>
      </Box>
    </Stack>
  );
};
